"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
// import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Wand2, Send, Loader2, CheckCircle, AlertCircle, X } from "lucide-react";
import {
  useWebSocket,
  useMagicEditorWebSocket,
} from "../../hooks/useWebSocket";
import { toast } from "sonner";

export default function MagicEditorWithWebSocket({
  soapNote,
  userId,
  token,
  isOpen,
  onClose,
  onStartProcessing,
  onUpdateSoapNote,
  aiService,
}) {
  const [userPrompt, setUserPrompt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInitialFeedback, setShowInitialFeedback] = useState(false);
  // --- For dragging ---
  const modalRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPos, setInitialPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.target.closest(".drag-handle")) {
      setDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialPos({ x: position.x, y: position.y });
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const onMouseMove = (e) => {
    if (dragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPosition({ x: initialPos.x + dx, y: initialPos.y + dy });
    }
  };

  const onMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  // WebSocket connection
  // const { isConnected, connectionStatus, error: connectionError } = useWebSocket(userId, token);

  // Magic Editor WebSocket
  const {
    status,
    progress,
    message,
    isProcessing,
    result,
    error: magicEditorError,
    reset,
  } = useMagicEditorWebSocket(soapNote?.soapNoteId, userId);

  useEffect(() => {
    if (result && result.soapNote) {
      toast.success("SOAP note updated successfully!");

      if (onUpdateSoapNote) {
        onUpdateSoapNote(result.soapNote);
      }
    }
  }, [result]);

  const handleSubmit = async () => {
    if (!userPrompt.trim() || !soapNote?.soapNoteId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setShowInitialFeedback(true);
    reset();

    if (onStartProcessing) {
      onStartProcessing();
    }

    if (onClose) {
      onClose();
    }

    try {
      const response = await aiService.correctSoapNote(
        soapNote.soapNoteId,
        userPrompt.trim()
      );
      console.log("API response:", response);

      if (response.status !== 200) {
        throw new Error(response.message || "Failed to update SOAP note");
      }

      toast.success(
        "Request sent! Magic AI Editor is now processing your request..."
      );
      setUserPrompt("");
      setShowInitialFeedback(false);
    } catch (error) {
      console.error("Error sending request:", error);
      toast.error("Failed to send request: " + error.message);
      setShowInitialFeedback(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "started":
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case "progress":
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case "complete":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "started":
        return "border-blue-200 bg-blue-50";
      case "progress":
        return "border-blue-200 bg-blue-50";
      case "complete":
        return "border-green-200 bg-green-50";
      case "error":
        return "border-red-200 bg-red-50";
      default:
        return "border-gray-200 bg-gray-50";
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 pointer-events-none">
      <div
        ref={modalRef}
        onMouseDown={onMouseDown}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: dragging ? "grabbing" : "default",
        }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full relative pointer-events-auto"
      >
        {/* Draggable Handle */}
        <div className="drag-handle absolute top-0 left-0 right-0 cursor-grab active:cursor-grabbing bg-gray-100 text-gray-600 text-center py-2 rounded-t-lg font-medium">
          Drag me anywhere ↕️
        </div>
        {/* Always-visible close control: the Cancel button can sit below the
            fold on short phones (e.g. iPhone SE). */}
        <button
          type="button"
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Close Magic AI Editor"
          title="Close"
          className="absolute right-1 top-0 z-10 flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="p-4 pt-10 sm:p-6 max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
          {/* Magic AI Editor Header */}
          <div className="flex items-center space-x-2 mb-4 mt-4">
            <Wand2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Magic AI Editor
            </h2>
          </div>

          {/* Magic AI Editor */}
          <div className="space-y-4">
            {/* Predefined Queries */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Quick Actions:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  "Make HPI more clear and paragraph-wise",
                  "Add more details to assessment",
                  "Improve plan with specific follow-up",
                  "Enhance objective findings",
                  "Clarify medication dosages",
                  "Add patient education points",
                ].map((query, index) => (
                  <button
                    key={index}
                    onClick={() => setUserPrompt(query)}
                    className={`text-left p-3 text-sm rounded-lg border transition-all duration-200 ${isSubmitting || isProcessing || showInitialFeedback
                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        : "bg-gray-50 hover:bg-blue-50 text-gray-700 border-gray-200 hover:border-blue-300 hover:shadow-sm"
                      }`}
                    disabled={
                      isSubmitting || isProcessing || showInitialFeedback
                    }
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>

            {/* Textarea */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Custom Request:
              </h4>
              <div className="flex space-x-2">
                <textarea
                  value={userPrompt}
                  onChange={(e) => {
                    if (e.target.value.length <= 1000) {
                      setUserPrompt(e.target.value);
                    }
                  }}
                  placeholder="Type your custom correction request..."
                  className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none min-h-[120px] sm:min-h-[200px] max-h-[300px] ${isSubmitting || isProcessing || showInitialFeedback
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                    }`}
                  maxLength={1000}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  disabled={isSubmitting || isProcessing || showInitialFeedback}
                />
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Press Ctrl+Enter (or Cmd+Enter on Mac) to submit
                </p>
                <p
                  className={`text-sm ${userPrompt.length > 500 ? "text-red-500" : "text-gray-500"
                    }`}
                >
                  {userPrompt.length}/1000 characters
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 [&>button]:w-full sm:[&>button]:w-auto">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting || isProcessing}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !userPrompt.trim() ||
                  isSubmitting ||
                  isProcessing ||
                  showInitialFeedback
                }
                className={`px-6 ${
                  !userPrompt.trim() ||
                  isSubmitting ||
                  isProcessing ||
                  showInitialFeedback
                    ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white shadow-lg transition-all duration-200"
                }`}
              >
                {isSubmitting || isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit
                  </>
                )}
              </Button>
            </div>

            {/* Real-time Status Updates */}
            {/* {(status || isProcessing || showInitialFeedback) && (
            <Card className={`border-2 ${getStatusColor()}`}>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon()}
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {showInitialFeedback && !status && 'Sending Request...'}
                        {status === 'started' && 'Magic AI Editor Initialized'}
                        {status === 'progress' && 'Processing Your Request'}
                        {status === 'complete' && 'Processing Complete'}
                        {status === 'error' && 'Processing Error'}
                      </div>
                      {showInitialFeedback && !message && (
                        <p className="text-sm text-gray-600 mt-1">Preparing your request for Magic AI Editor...</p>
                      )}
                      {message && (
                        <p className="text-sm text-gray-600 mt-1">{message}</p>
                      )}
                    </div>
                    {progress > 0 && (
                      <span className="text-sm text-gray-500">
                        Step {Math.ceil(progress / 10)} of 9
                      </span>
                    )}
                  </div>

                  {progress > 0 && (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span>Overall Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${progress}%` }}
                        />
                      </div> */}

            {/* Step indicators */}
            {/* <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className={`p-2 rounded ${progress >= 10 ? 'bg-green-100 text-green-700' : progress >= 5 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          Validate
                        </div>
                        <div className={`p-2 rounded ${progress >= 30 ? 'bg-green-100 text-green-700' : progress >= 15 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          Retrieve
                        </div>
                        <div className={`p-2 rounded ${progress >= 50 ? 'bg-green-100 text-green-700' : progress >= 35 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          AI Process
                        </div>
                        <div className={`p-2 rounded ${progress >= 70 ? 'bg-green-100 text-green-700' : progress >= 55 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          Parse
                        </div>
                        <div className={`p-2 rounded ${progress >= 90 ? 'bg-green-100 text-green-700' : progress >= 75 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          Save
                        </div>
                        <div className={`p-2 rounded ${progress >= 100 ? 'bg-green-100 text-green-700' : progress >= 95 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          Complete
                        </div>
                      </div>
                    </div>
                  )}

                  {result && (
                    <div className="mt-4 p-3 bg-green-100 rounded-lg">
                      <p className="text-sm font-medium text-green-800 mb-2">
                        SOAP Note Updated Successfully!
                      </p>
                      {result.correctionDetails && (
                        <div className="text-sm text-green-700">
                          <p>Version: {result.correctionDetails.version}</p>
                          <p>Updated at: {new Date(result.correctionDetails.correctedAt).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {magicEditorError && (
                    <div className="mt-4 p-3 bg-red-100 rounded-lg">
                      <p className="text-sm font-medium text-red-800">
                        Error: {magicEditorError}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )} */}
          </div>
        </div>
      </div>
    </div>
  );
}
